import type { NamedLLMConfig } from "@/services/api.service";

// The gateway reads these named llm_configs entries; only ai-tutor TVs (saved with their
// gateway sibling) get the Notepad Models card.
export const NOTEPAD_MODELS_APP_ID = "ai-tutor";

export interface NotepadModelRole {
  name: string;
  label: string;
  emptyLabel: string;
  // The gateway tutor prompt assumes private reasoning, so that row offers only such models.
  needsPrivateReasoning?: boolean;
}

export const NOTEPAD_MODEL_ROLES: NotepadModelRole[] = [
  { name: "notepad", label: "Notepad agent", emptyLabel: "Gateway default", needsPrivateReasoning: true },
  { name: "notepad-diagram", label: "Diagram", emptyLabel: "Gateway default" },
  { name: "notepad-diagram-edit", label: "Diagram edit", emptyLabel: "Same as diagram" },
  { name: "notepad-verify", label: "Verifier", emptyLabel: "Gateway default" },
];

// Mirrors gateway src/shared/models.ts (OpenAI cards served from Azure, Gemini from Vertex).
// privateReasoning: OpenAI effort above `none` or Gemini `thinking: true`, as set there.
export const GATEWAY_MODEL_CATALOG: { model: string; privateReasoning: boolean }[] = [
  { model: "vertex_ai/gemini-3.5-flash-lite", privateReasoning: false },
  { model: "vertex_ai/gemini-3.6-flash", privateReasoning: false },
  { model: "vertex_ai/gemini-3.7-flash", privateReasoning: false },
  { model: "vertex_ai/gemini-3.8-flash", privateReasoning: false },
  { model: "azure/gpt-5.4", privateReasoning: false },
  { model: "azure/gpt-5.6-terra", privateReasoning: true },
  { model: "azure/gpt-5.6-sol", privateReasoning: true },
  { model: "azure/gpt-5.6-luna", privateReasoning: true },
  { model: "azure/gpt-6-luna", privateReasoning: true },
  { model: "azure/gpt-6-sol", privateReasoning: true },
  { model: "azure/gpt-5.2", privateReasoning: false },
  { model: "azure/gpt-5-nano", privateReasoning: true },
  { model: "azure/gpt-5.4-mini", privateReasoning: true },
];

// Derive the provider from a litellm model string ("deepseek/deepseek-chat" -> "deepseek").
export const providerFromModel = (model: string) => {
  const slashIdx = model.indexOf("/");
  return slashIdx > 0 ? model.slice(0, slashIdx) : "litellm";
};

const bareName = (model: string) => model.slice(model.lastIndexOf("/") + 1);

// Same match as the gateway's gatewayModelName: the provider prefix is ignored.
const catalogCard = (model: string) =>
  GATEWAY_MODEL_CATALOG.find((c) => bareName(c.model) === bareName(model));

export const modelOptionsFor = (role: NotepadModelRole) =>
  GATEWAY_MODEL_CATALOG
    .filter((c) => !role.needsPrivateReasoning || c.privateReasoning)
    .map((c) => c.model);

export const gatewayModelIssue = (model: string, role: NotepadModelRole): string | null => {
  const card = catalogCard(model);
  if (!card) return "unsupported by the gateway";
  if (role.needsPrivateReasoning && !card.privateReasoning) return "no private reasoning";
  return null;
};

const RESERVED_NAMES = new Set<string>(NOTEPAD_MODEL_ROLES.map((r) => r.name));

export const splitNotepadModels = (configs: NamedLLMConfig[] | undefined, applicationId: string) => {
  const all = configs ?? [];
  if (applicationId !== NOTEPAD_MODELS_APP_ID) return { generic: all, notepad: [] };
  return {
    generic: all.filter((c) => !RESERVED_NAMES.has(c.name)),
    notepad: all.filter((c) => RESERVED_NAMES.has(c.name)),
  };
};

// The card owns the reserved names, so a generic entry that reuses one is dropped.
export const mergeNotepadModels = (
  generic: NamedLLMConfig[],
  notepad: NamedLLMConfig[],
  applicationId: string,
) =>
  applicationId === NOTEPAD_MODELS_APP_ID
    ? [...generic.filter((c) => !RESERVED_NAMES.has(c.name)), ...notepad]
    : generic;

// An unchanged entry is kept as stored; only the edited fields are rewritten.
export const setNotepadModel = (
  notepad: NamedLLMConfig[],
  name: string,
  model: string,
): NamedLLMConfig[] => {
  if (!model) return notepad.filter((c) => c.name !== name);
  const existing = notepad.find((c) => c.name === name);
  if (!existing) return [...notepad, { name, config: { provider: providerFromModel(model), model } }];
  return notepad.map((c) =>
    c.name === name ? { name, config: { ...c.config, provider: providerFromModel(model), model } } : c,
  );
};

export const setNotepadFallbacks = (
  notepad: NamedLLMConfig[],
  name: string,
  fallbacks: string[],
): NamedLLMConfig[] =>
  notepad.map((c) => {
    if (c.name !== name) return c;
    const config = { ...c.config };
    delete config.fallbacks;
    const tiers = fallbacks.filter(Boolean).map((model) => ({ provider: providerFromModel(model), model }));
    return { name, config: tiers.length ? { ...config, fallbacks: tiers } : config };
  });
